import axios, { AxiosInstance } from 'axios';
import type { PaymentMethod, RawSession, LoginResponse, ResetPasswordResponse } from '@app-types';
import { authStore } from '@stores';

interface AccountPayorPreferences {
    isAutoPay?: boolean;
    dueDay?: number | null;
}

interface PaymentMethodQuery {
    isActive?: boolean;
    withAccountAssignments?: boolean;
}

interface NewPayor {
    email: string;
    fullName: string;
}

interface EnrollmentPayorData {
    idPayor: number;
    email: string;
    fullName: string;
    payAmount: number;
    payPercentage: number;
    dueDay: number | null;
    amountType: number;
    idInvitation?: string;
}

class SunboundPayProxy {

    _axios!: AxiosInstance;

    init(baseURL: string) {

        this._axios = axios.create({
            baseURL,
            withCredentials: true
        });

        // Authenticate even on browsers blocking cross-origin cookies (Safari)
        this._axios.interceptors.request.use(config => {
            if (authStore.token) {
                config.headers.Authorization = `Bearer ${authStore.token}`;
            }
            return config;
        });

        this._axios.interceptors.response.use(
            response => response,
            error => {
                const isInvalidSession = error?.response?.status === 401 && error?.response?.data?.message === `Invalid session`;
                if (isInvalidSession) {
                    authStore.setUnauthorized();
                }
                return Promise.reject(error);
            }
        );
    }

    async login(email: string, password: string): Promise<LoginResponse | void> {
        const response = await this._axios.post(`/login`, {
            email, password
        });
        const contentType = response.headers[`content-type`];
        if (typeof contentType === `string` && contentType.includes(`application/json`)) {
            const data = response.data as LoginResponse;
            if (data.token && data.tokenExpiresAt) {
                authStore.setSession(data.token, data.tokenExpiresAt);
            }
            return data;
        }
    }

    async logout() {
        const response = await this._axios.post(`/logout`, {});
        authStore.clearSession();
        return response.data;
    }

    async issueTransferToken(): Promise<{ transferToken: string }> {
        const response = await this._axios.post(`/auth/transfer-token`);
        return response.data;
    }

    async redeemTransferToken(transferToken: string): Promise<{ token: string; tokenExpiresAt: string }> {
        const response = await this._axios.post(`/auth/redeem-transfer-token`, { transferToken });
        return response.data;
    }

    async updatePassword(password: string) {
        const response = await this._axios.patch(`/payor/password`, {
            password
        });
        return response.data;
    }

    async resetPassword(password: string, sessionId: string): Promise<ResetPasswordResponse> {
        const response = await this._axios.patch(`/payor/reset-password`, {
            password
        }, {
            headers: {
                sessionId
            }
        });
        const data = response.data as ResetPasswordResponse;
        if (data.token && data.tokenExpiresAt) {
            authStore.setSession(data.token, data.tokenExpiresAt);
        }
        return data;
    }

    async resetPasswordCheck(sessionId: string) {
        const response = await this._axios.get(`/payor/reset-password`, {
            headers: {
                sessionId
            }
        });
        return response.data;
    }

    async pay(amount: number, idInvitation: string, idempotencyKey: string) {
        const response = await this._axios.post(`/invitations/${idInvitation}/payments`, {
            amount
        }, {
            headers: {
                idempotencyKey
            }
        });
        return response.data;
    }

    async addPaymentMethod(idAccount: number, token: string, isDefault: boolean) {
        const response = await this._axios.post(`/accounts/${idAccount}/payor/payment-methods`, {
            token,
            isDefault
        });
        return response.data;
    }

    async getSession(sessionId: string): Promise<RawSession> {
        const response = await this._axios.get(`/session`, {
            headers: {
                sessionId
            }
        });
        const data = response.data as RawSession;
        if (data.sessionId && data.sessionExpiresAt) {
            authStore.setSession(data.sessionId, data.sessionExpiresAt);
        }
        return data;
    }

    async refreshSession(): Promise<RawSession> {
        const response = await this._axios.get(`/validate-session`);
        return response.data;
    }

    async getGuarantorTasks(idAccount: number) {
        const response = await this._axios.get(`/guarantor/accounts/${idAccount}/tasks`);
        return response.data;
    }

    async updateAccountPayorPreferences(idAccount: number, preferences: AccountPayorPreferences) {
        const response = await this._axios.patch(`/accounts/${idAccount}/payor/account-preferences`, preferences);
        return response.data;
    }

    async updatePayorPreferences(idPreference: number, value: number) {
        const response = await this._axios.put(`/payor/preferences`, {
            idPreference,
            value
        });
        return response.data;
    }

    async disablePayor(idAccount: number, idPayor: number) {
        const response = await this._axios.patch(`/guarantor/accounts/${idAccount}/payors/${idPayor}/disable`, {
            isActive: false
        });
        return response.data;
    }

    async enablePayor(idAccount: number, idPayor: number) {
        const response = await this._axios.patch(`/guarantor/accounts/${idAccount}/payors/${idPayor}/enable`, {
            isActive: true
        });
        return response.data;
    }

    async getAccountPayors(idAccount: number, options: { includeInactive?: boolean } = {}) {
        const response = await this._axios.get(`/guarantor/accounts/${idAccount}/payors`, {
            params: options
        });
        return response.data;
    }

    async getPayorTasks(idAccount: number) {
        const response = await this._axios.get(`/accounts/${idAccount}/payor/tasks`);
        return response.data;
    }

    async getPayorCheck(email: string) {
        const response = await this._axios.get(`/payor-check`, {
            params: {
                email
            }
        });
        return response.data;
    }

    async createPayor(idAccount: number, newPayor: NewPayor) {
        const response = await this._axios.post(`/guarantor/account/${idAccount}/payors`, {
            ...newPayor
        });
        return response.data;
    }

    async updateEnrollment(idAccount: number, idInvoice: number, idProductType: number, enrollment: EnrollmentPayorData[]) {
        const response = await this._axios.patch(`/guarantor/accounts/${idAccount}/invoices/${idInvoice}/enroll`, {
            idProductType,
            enrollment
        });
        return response.data;
    }

    async deactivatePaymentMethod(idAccount: number, idPaymentMethod: number) {
        const response = await this._axios.patch(`/accounts/${idAccount}/payor/payment-methods/${idPaymentMethod}/deactivate`);
        return response.data;
    }

    async upsertAccountDefaultPaymentMethod(idAccount: number, idPaymentMethod: number) {
        const response = await this._axios.patch(`/accounts/${idAccount}/payor/payment-methods`, {
            defaultPaymentMethod: idPaymentMethod
        });
        return response.data;
    }

    async getPayorPaymentMethods(query: PaymentMethodQuery): Promise<PaymentMethod[]> {
        const response = await this._axios.get(`/payor/payment-methods`, { params: query });
        return response.data;
    }

    async getPaymentSettings(idAccount: number) {
        const response = await this._axios.get(`/accounts/${idAccount}/payor/payment-settings`);
        return response.data;
    }

    async getPayments(idAccount: number) {
        const response = await this._axios.get(`/accounts/${idAccount}/payor/payments`);
        return response.data;
    }

    async sendPasswordReset(email: string) {
        const response = await this._axios.post(`/payor/send-password-reset`, {
            email
        });
        return response.data;
    }

    async resendWelcome(email: string) {
        const response = await this._axios.post(`/payor/resend-welcome`, {
            email
        });
        return response.data;
    }

    getInvoiceDocumentUrl(idAccount: number, idInvoice: number) {
        return `${this._axios.defaults.baseURL}/accounts/${idAccount}/payor/invoices/${idInvoice}/document/view`;
    }
}

const sunboundPayProxy = new SunboundPayProxy();

export default sunboundPayProxy;
